import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, MapPin, User, Plus, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import RegisterPopup from '../components/RegisterPopup';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const Appointments = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [appointmentList, setAppointmentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    date: '',
    time: '',
    type: '',
    doctor: '',
    location: ''
  });

  // Fetch appointments from database
  const fetchAppointments = async () => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/appointments/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setAppointmentList(data);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Không thể tải lịch hẹn");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setShowRegisterPopup(true);
      setLoading(false);
    } else {
      fetchAppointments();
    }
  }, [isLoggedIn]);

  const appointmentTypes = [
    'Khám thai định kỳ',
    'Siêu âm',
    'Siêu âm 4D',
    'Xét nghiệm',
    'Tư vấn dinh dưỡng',
    'Khác'
  ];

  const handleCreateAppointment = async () => {
    if (!newAppointment.date || !newAppointment.time || !newAppointment.type) {
      toast.error('Vui lòng điền đầy đủ thông tin!');
      return;
    }

    const userId = localStorage.getItem("user_id");
    if (!userId) {
      toast.error("Vui lòng đăng nhập để đặt lịch");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: parseInt(userId),
          date: newAppointment.date,
          time: newAppointment.time,
          type: newAppointment.type,
          doctor: newAppointment.doctor || null,
          location: newAppointment.location || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        setAppointmentList([...appointmentList, data.appointment]);
        setNewAppointment({ date: '', time: '', type: '', doctor: '', location: '' });
        setIsDialogOpen(false);
        toast.success('Đặt lịch thành công!');
      } else {
        toast.error('Không thể đặt lịch');
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error('Lỗi kết nối máy chủ');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      const res = await fetch(`${API_BASE}/appointments/${appointmentId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        setAppointmentList(appointmentList.filter(a => a.id !== appointmentId));
        toast.success('Đã hủy lịch hẹn');
      } else {
        toast.error('Không thể hủy lịch hẹn');
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast.error('Lỗi kết nối máy chủ');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'confirmed') {
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Đã xác nhận
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-200">
        <AlertCircle className="w-3 h-3 mr-1" />
        Chờ xác nhận
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-pink-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
              Lịch hẹn khám
            </span>
          </h1>
          <p className="text-2xl text-gray-600">
            Quản lý lịch khám thai và tư vấn của bạn
          </p>
        </div>

        {/* Create Appointment Button */}
        <div className="mb-8 flex justify-end">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white">
                <Plus className="w-5 h-5 mr-2" />
                Đặt lịch mới
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl text-gray-800">Đặt lịch hẹn mới</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date" className="text-gray-700">Ngày</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newAppointment.date}
                      onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })}
                      className="border-pink-200 focus:border-pink-400 mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time" className="text-gray-700">Giờ</Label>
                    <Input
                      id="time"
                      type="time"
                      value={newAppointment.time}
                      onChange={(e) => setNewAppointment({ ...newAppointment, time: e.target.value })}
                      className="border-pink-200 focus:border-pink-400 mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="type" className="text-gray-700">Loại lịch hẹn</Label>
                  <select
                    id="type"
                    value={newAppointment.type}
                    onChange={(e) => setNewAppointment({ ...newAppointment, type: e.target.value })}
                    className="w-full p-2 border border-pink-200 rounded-md focus:outline-none focus:border-pink-400 mt-1"
                  >
                    <option value="">Chọn loại lịch hẹn</option>
                    {appointmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="doctor" className="text-gray-700">Bác sĩ</Label>
                  <Input
                    id="doctor"
                    placeholder="Nhập tên bác sĩ"
                    value={newAppointment.doctor}
                    onChange={(e) => setNewAppointment({ ...newAppointment, doctor: e.target.value })}
                    className="border-pink-200 focus:border-pink-400 mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="location" className="text-gray-700">Địa điểm</Label>
                  <Input
                    id="location"
                    placeholder="Nhập địa điểm khám"
                    value={newAppointment.location}
                    onChange={(e) => setNewAppointment({ ...newAppointment, location: e.target.value })}
                    className="border-pink-200 focus:border-pink-400 mt-1"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Hủy
                  </Button>
                  <Button
                    onClick={handleCreateAppointment}
                    className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white"
                  >
                    Đặt lịch
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="md:col-span-2 flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
            <span className="ml-2 text-gray-600">Đang tải lịch hẹn...</span>
          </div>
        )}

        {/* Appointments List */}
        <div className="grid md:grid-cols-2 gap-6">
          {!loading && appointmentList.length > 0 ? (
            appointmentList.map((appointment) => (
              <Card key={appointment.id} className="border-pink-100 shadow-md hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl text-gray-800">{appointment.type}</CardTitle>
                    {getStatusBadge(appointment.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Ngày</p>
                      <p className="font-semibold">{new Date(appointment.date).toLocaleDateString('vi-VN')}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Giờ</p>
                      <p className="font-semibold">{appointment.time}</p>
                    </div>
                  </div>

                  {appointment.doctor && (
                    <div className="flex items-center space-x-3 text-gray-700">
                      <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Bác sĩ</p>
                        <p className="font-semibold">{appointment.doctor}</p>
                      </div>
                    </div>
                  )}

                  {appointment.location && (
                    <div className="flex items-center space-x-3 text-gray-700">
                      <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Địa điểm</p>
                        <p className="font-semibold">{appointment.location}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-2 pt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1 border-pink-300 text-pink-600 hover:bg-pink-50"
                      onClick={() => toast.info('Tính năng đang phát triển')}
                    >
                      Chỉnh sửa
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteAppointment(appointment.id)}
                    >
                      Hủy lịch
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-pink-100 p-12 md:col-span-2">
              <div className="text-center text-gray-500">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-pink-300" />
                <p className="text-lg mb-4">Chưa có lịch hẹn nào</p>
                <Button
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white"
                >
                  Đặt lịch ngay
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Footer />

      {/* Register Popup */}
      {!isLoggedIn && (
        <RegisterPopup isOpen={showRegisterPopup} onClose={() => {
          setShowRegisterPopup(false);
        }} />
      )}
    </div>
  );
};

export default Appointments;